/**
 * Grafo_Entidades en memoria, derivado de los JSONs reales de
 * Antofagasta (`frontend/public/data/*.json`) y de entidades sintéticas
 * derivadas por DMA (Valvulas, PRVs, Cuadrillas, puntos de Presion).
 *
 * Implementa la interfaz {@link Graph} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5). El grafo:
 *   - representa como nodos las entidades operacionales y como aristas
 *     sus relaciones (R11.1);
 *   - modela la jerarquía en que una DMA agrupa sus Medidores, Gateways,
 *     Valvulas, PRVs, Cuadrillas, Casos, puntos de Presion,
 *     Ordenes_Inspeccion y Anomalias mediante aristas "contiene"
 *     (R11.2);
 *   - se mantiene consistente reaccionando a eventos vía
 *     {@link GrafoEntidadesLocal.upsertNode}/`upsertEdge` (R11.3);
 *   - permite recorrer relaciones directas e indirectas por profundidad
 *     acotada `[1, MAX]` (R11.4, R11.6);
 *   - sirve de sustrato de consulta **sin duplicar** las entidades de la
 *     fundación: las referencia por `entidad_ref` (R11.5, R17.1).
 *
 * Separación carga ↔ construcción: {@link buildGraphFromData} es una
 * función **pura** que acepta los datos como parámetro, de modo que el
 * grafo pueda construirse tanto desde datos cargados en el navegador
 * (vía `../api/realData`) como desde datos inyectados en tests
 * Node/Vitest. El fetch real (`loadRealData`) se mantiene fuera de este
 * módulo.
 *
 * Las funciones puras {@link buildGraphFromData}, {@link bfsNeighbors} y
 * {@link rootOf} se exportan para que las tareas de testing (Properties
 * 28 y 29) las ejerciten sin instanciar la clase.
 */

import type {
  Arista,
  Coordenada,
  EntidadRef,
  EventoOperacional,
  Graph,
  Id,
  Nodo,
  Result,
  Subgrafo,
  TipoArista,
  TipoEntidad,
} from "../api/contracts";

// Tipos de los JSONs reales (solo lectura; no se redefine su esquema).
import type { RealAlarma, RealDma, RealGateway } from "../api/realData";

// ---------------------------------------------------------------------------
// Configuración
// ---------------------------------------------------------------------------

/** Profundidad máxima configurable por defecto para `neighbors` (R11.4). */
export const MAX_PROFUNDIDAD_DEFAULT = 6;

/** Parámetros de construcción y recorrido del Grafo_Entidades. */
export interface GraphConfig {
  /** Profundidad máxima permitida en un recorrido BFS (R11.4, R11.6). */
  maxProfundidad: number;
  /** Valvulas sintéticas derivadas por DMA. */
  valvulasPorDma: number;
  /** PRVs sintéticas derivadas por DMA. */
  prvsPorDma: number;
  /** Cuadrillas sintéticas derivadas por DMA. */
  cuadrillasPorDma: number;
  /** Puntos de Presion sintéticos derivados por DMA. */
  puntosPresionPorDma: number;
}

/** Configuración por defecto del Grafo_Entidades demo. */
export const GRAPH_CONFIG_DEFAULT: GraphConfig = {
  maxProfundidad: MAX_PROFUNDIDAD_DEFAULT,
  valvulasPorDma: 2,
  prvsPorDma: 1,
  cuadrillasPorDma: 1,
  puntosPresionPorDma: 1,
};

// ---------------------------------------------------------------------------
// Datos de entrada (forma de los JSONs reales, inyectables en tests)
// ---------------------------------------------------------------------------

/**
 * Datos crudos desde los que se construye el grafo. Coincide con la
 * forma de `dmas.json`, `gateways.json` y `alarmas.json` cargados por
 * `../api/realData`. `gateways`/`alarmas` son opcionales para construir
 * grafos parciales en tests.
 */
export interface GraphData {
  dmas: RealDma[];
  gateways?: RealGateway[];
  alarmas?: RealAlarma[];
}

/** Proyección pura del grafo: listas planas de nodos y aristas. */
export interface ProyeccionGrafo {
  nodos: Nodo[];
  aristas: Arista[];
}

// ---------------------------------------------------------------------------
// Prefijos de node_id y jerarquía de tipos
// ---------------------------------------------------------------------------

/** Prefijo determinista del `node_id` por tipo de entidad. */
const PREFIJO_NODO: Record<TipoEntidad, string> = {
  Cliente: "cli",
  Medidor: "med",
  DMA: "dma",
  Gateway: "gw",
  Anomalia: "anom",
  Alerta: "alerta",
  Orden_Inspeccion: "orden",
  Caso: "caso",
  Valvula: "val",
  PRV: "prv",
  Cuadrilla: "crew",
  Presion: "pres",
  Insight: "insight",
};

/**
 * Nivel de jerarquía por tipo de entidad: a menor número, mayor
 * jerarquía. La DMA agrupa al resto, por lo que ocupa el nivel 0
 * (R11.2, R3.1). Los demás tipos quedan en niveles inferiores con un
 * orden total determinista que desempata `rootOf`.
 */
const NIVEL_JERARQUIA: Record<TipoEntidad, number> = {
  DMA: 0,
  Gateway: 1,
  Cuadrilla: 1,
  PRV: 2,
  Valvula: 2,
  Caso: 3,
  Presion: 3,
  Orden_Inspeccion: 4,
  Medidor: 4,
  Anomalia: 5,
  Alerta: 5,
  Cliente: 5,
  Insight: 5,
};

/** Calcula el `node_id` determinista de una referencia de entidad. */
export function nodeIdFor(ref: EntidadRef): Id {
  return `${PREFIJO_NODO[ref.tipo]}:${ref.id}`;
}

// ---------------------------------------------------------------------------
// Helpers de construcción
// ---------------------------------------------------------------------------

/** Tipos de alarma de `alarmas.json` que se proyectan como Anomalias. */
const TIPOS_ALARMA: Array<{ campo: keyof RealAlarma; etiqueta: string }> = [
  { campo: "alarm_leakage", etiqueta: "fuga" },
  { campo: "alarm_burst", etiqueta: "reventon" },
  { campo: "alarm_backflow", etiqueta: "backflow" },
  { campo: "alarm_dry", etiqueta: "tuberia_seca" },
  { campo: "alarm_manipulation", etiqueta: "manipulacion" },
  { campo: "alarm_battery", etiqueta: "bateria" },
  { campo: "alarm_permanent", etiqueta: "permanente" },
];

/**
 * Desplazamiento determinista para georreferenciar entidades sintéticas
 * alrededor de la coordenada de su DMA, evitando solaparlas.
 */
function offsetCoord(base: Coordenada, indice: number): Coordenada {
  const paso = 0.002;
  return {
    lon: base.lon + paso * ((indice % 3) - 1),
    lat: base.lat + paso * (Math.floor(indice / 3) - 1),
  };
}

function nodoDma(dma: RealDma): Nodo {
  return {
    node_id: nodeIdFor({ tipo: "DMA", id: dma.codigo }),
    tipo: "DMA",
    entidad_ref: { tipo: "DMA", id: dma.codigo },
    coord: { lon: dma.lon, lat: dma.lat },
    attrs: {
      nombre: dma.nombre,
      clientes: dma.clientes,
      nrw_pct: dma.nrw_pct,
    },
  };
}

function arista(origen_id: Id, destino_id: Id, tipo: TipoArista, attrs?: Record<string, unknown>): Arista {
  return attrs ? { origen_id, destino_id, tipo, attrs } : { origen_id, destino_id, tipo };
}

// ---------------------------------------------------------------------------
// Construcción pura del grafo desde datos
// ---------------------------------------------------------------------------

/**
 * Construye la proyección del Grafo_Entidades a partir de los datos
 * crudos. Función **pura** y determinista (no hace fetch ni accede a
 * estado global): mismos datos → mismos nodos/aristas, en el mismo
 * orden. No duplica entidades de la fundación; las referencia por
 * `entidad_ref` (R11.1, R11.5, R17.1).
 */
export function buildGraphFromData(data: GraphData, config: GraphConfig = GRAPH_CONFIG_DEFAULT): ProyeccionGrafo {
  const nodos: Nodo[] = [];
  const aristas: Arista[] = [];
  const vistos = new Set<Id>();

  const dmas = data.dmas ?? [];
  const gateways = data.gateways ?? [];
  const alarmas = data.alarmas ?? [];

  const pushNodo = (n: Nodo): void => {
    if (vistos.has(n.node_id)) return;
    vistos.add(n.node_id);
    nodos.push(n);
  };

  // 1) DMAs (nodo raíz de la jerarquía) ------------------------------------
  dmas.forEach((dma) => {
    const dmaNode = nodoDma(dma);
    pushNodo(dmaNode);

    const base: Coordenada = { lon: dma.lon, lat: dma.lat };

    // 1a) Valvulas sintéticas por DMA.
    for (let i = 0; i < config.valvulasPorDma; i++) {
      const id = `${dma.codigo}-V${i + 1}`;
      const ref: EntidadRef = { tipo: "Valvula", id };
      const node_id = nodeIdFor(ref);
      pushNodo({ node_id, tipo: "Valvula", entidad_ref: ref, coord: offsetCoord(base, i), attrs: { estado: "abierta" } });
      aristas.push(arista(dmaNode.node_id, node_id, "contiene"));
    }

    // 1b) PRVs sintéticas por DMA.
    for (let i = 0; i < config.prvsPorDma; i++) {
      const id = `${dma.codigo}-PRV${i + 1}`;
      const ref: EntidadRef = { tipo: "PRV", id };
      const node_id = nodeIdFor(ref);
      pushNodo({ node_id, tipo: "PRV", entidad_ref: ref, coord: offsetCoord(base, i + 3), attrs: { setpoint: 35 } });
      aristas.push(arista(dmaNode.node_id, node_id, "contiene"));
    }

    // 1c) Cuadrillas sintéticas por DMA.
    for (let i = 0; i < config.cuadrillasPorDma; i++) {
      const id = `${dma.codigo}-C${i + 1}`;
      const ref: EntidadRef = { tipo: "Cuadrilla", id };
      const node_id = nodeIdFor(ref);
      pushNodo({ node_id, tipo: "Cuadrilla", entidad_ref: ref, coord: offsetCoord(base, i + 6), attrs: { estado: "disponible" } });
      aristas.push(arista(dmaNode.node_id, node_id, "contiene"));
    }

    // 1d) Puntos de Presion sintéticos por DMA.
    for (let i = 0; i < config.puntosPresionPorDma; i++) {
      const id = `${dma.codigo}-P${i + 1}`;
      const ref: EntidadRef = { tipo: "Presion", id };
      const node_id = nodeIdFor(ref);
      pushNodo({ node_id, tipo: "Presion", entidad_ref: ref, coord: offsetCoord(base, i + 9), attrs: { unidad: "mca" } });
      aristas.push(arista(dmaNode.node_id, node_id, "contiene"));
    }
  });

  // 2) Gateways: asignados de forma determinista a una DMA ------------------
  //    (los JSONs no traen DMA del gateway; se reparte round-robin sobre las
  //    DMAs existentes para materializar la jerarquía DMA contiene Gateway).
  gateways.forEach((gw, idx) => {
    const ref: EntidadRef = { tipo: "Gateway", id: gw.gateway_id };
    const node_id = nodeIdFor(ref);
    pushNodo({
      node_id,
      tipo: "Gateway",
      entidad_ref: ref,
      attrs: { medidores: gw.medidores, avg_sf: gw.avg_sf, avg_rssi: gw.avg_rssi },
    });
    if (dmas.length > 0) {
      const dma = dmas[idx % dmas.length];
      aristas.push(arista(nodeIdFor({ tipo: "DMA", id: dma.codigo }), node_id, "contiene"));
    }
  });

  // 3) Medidores y Anomalias derivados de alarmas.json ----------------------
  alarmas.forEach((al) => {
    if (!Number.isInteger(al.dma_idx) || al.dma_idx < 0 || al.dma_idx >= dmas.length) {
      // Sin DMA válida asociada: se omite (dato incompleto). El registro
      // en Observabilidad lo realiza la capa de mapa (tarea 4.1).
      return;
    }
    const dma = dmas[al.dma_idx];
    const dmaNodeId = nodeIdFor({ tipo: "DMA", id: dma.codigo });

    // Medidor (referencia a la entidad de la fundación por su device id).
    const medRef: EntidadRef = { tipo: "Medidor", id: al.device };
    const medNodeId = nodeIdFor(medRef);
    pushNodo({ node_id: medNodeId, tipo: "Medidor", entidad_ref: medRef, attrs: { volumen_total: al.total_volume } });
    aristas.push(arista(dmaNodeId, medNodeId, "contiene"));

    // Anomalias por cada flag de alarma activo.
    TIPOS_ALARMA.forEach(({ campo, etiqueta }) => {
      if (al[campo] !== true) return;
      const anomId = `${al.device}:${etiqueta}`;
      const anomRef: EntidadRef = { tipo: "Anomalia", id: anomId };
      const anomNodeId = nodeIdFor(anomRef);
      pushNodo({
        node_id: anomNodeId,
        tipo: "Anomalia",
        entidad_ref: anomRef,
        attrs: { tipo_alarma: etiqueta, ts: al.root_timestamp },
      });
      // La DMA agrupa la Anomalia (jerarquía) y el Medidor la origina.
      aristas.push(arista(dmaNodeId, anomNodeId, "contiene"));
      aristas.push(arista(medNodeId, anomNodeId, "origina"));
    });
  });

  return { nodos, aristas };
}

// ---------------------------------------------------------------------------
// Índice de adyacencia y recorrido BFS
// ---------------------------------------------------------------------------

/** Índice del grafo optimizado para recorridos. */
interface IndiceGrafo {
  nodos: Map<Id, Nodo>;
  /** Adyacencia **no dirigida** para recorrer relaciones en ambos sentidos. */
  adyacencia: Map<Id, Set<Id>>;
  aristas: Arista[];
}

function construirIndice(proyeccion: ProyeccionGrafo): IndiceGrafo {
  const nodos = new Map<Id, Nodo>();
  for (const n of proyeccion.nodos) nodos.set(n.node_id, n);

  const adyacencia = new Map<Id, Set<Id>>();
  const agregar = (a: Id, b: Id): void => {
    let set = adyacencia.get(a);
    if (!set) {
      set = new Set<Id>();
      adyacencia.set(a, set);
    }
    set.add(b);
  };
  for (const e of proyeccion.aristas) {
    agregar(e.origen_id, e.destino_id);
    agregar(e.destino_id, e.origen_id);
  }

  return { nodos, adyacencia, aristas: proyeccion.aristas };
}

/**
 * Recorrido BFS del Grafo_Entidades acotado por profundidad (R11.4,
 * R11.6, Property 28). Devuelve el subgrafo con todos los nodos
 * alcanzables a `profundidad` saltos del nodo inicial (incluido) y las
 * aristas entre ellos.
 *
 * Validaciones de rango (sin iniciar el recorrido):
 *   - `profundidad` debe ser entero;
 *   - `profundidad < 1` o `profundidad > max` → `Result` de error.
 * Si el nodo inicial no existe, devuelve error.
 */
export function bfsNeighbors(
  grafo: ProyeccionGrafo,
  node_id: Id,
  profundidad: number,
  max: number = MAX_PROFUNDIDAD_DEFAULT,
): Result<Subgrafo> {
  if (!Number.isInteger(profundidad) || profundidad < 1 || profundidad > max) {
    return {
      ok: false,
      error: `Profundidad ${profundidad} fuera del rango permitido [1, ${max}].`,
    };
  }

  const indice = construirIndice(grafo);
  if (!indice.nodos.has(node_id)) {
    return { ok: false, error: `Nodo ${node_id} no encontrado en el Grafo_Entidades.` };
  }

  // BFS determinista: los vecinos se procesan en orden ascendente de id.
  const visitados = new Set<Id>([node_id]);
  let frontera: Id[] = [node_id];

  for (let nivel = 0; nivel < profundidad && frontera.length > 0; nivel++) {
    const siguiente: Id[] = [];
    for (const actual of frontera) {
      const vecinos = indice.adyacencia.get(actual);
      if (!vecinos) continue;
      for (const v of [...vecinos].sort()) {
        if (!visitados.has(v)) {
          visitados.add(v);
          siguiente.push(v);
        }
      }
    }
    frontera = siguiente;
  }

  const nodos: Nodo[] = [];
  for (const id of [...visitados].sort()) {
    const n = indice.nodos.get(id);
    if (n) nodos.push(n);
  }

  const aristas = indice.aristas.filter((e) => visitados.has(e.origen_id) && visitados.has(e.destino_id));

  return { ok: true, value: { nodos, aristas } };
}

// ---------------------------------------------------------------------------
// rootOf — entidad de mayor jerarquía
// ---------------------------------------------------------------------------

/**
 * Devuelve el `node_id` de la entidad de **mayor jerarquía** entre las
 * dadas (la DMA agrupa al resto), usada como entidad raíz de un Caso
 * (R3.1). Determinista: a igual nivel de jerarquía, desempata por
 * `node_id` ascendente. Lista vacía → `Result` de error.
 */
export function rootOf(entidades: EntidadRef[]): Result<Id> {
  if (!entidades || entidades.length === 0) {
    return { ok: false, error: "No se puede determinar la entidad raíz de un conjunto vacío." };
  }

  let mejor: { node_id: Id; nivel: number } | null = null;
  for (const ref of entidades) {
    const nivel = NIVEL_JERARQUIA[ref.tipo];
    const node_id = nodeIdFor(ref);
    if (
      mejor === null ||
      nivel < mejor.nivel ||
      (nivel === mejor.nivel && node_id < mejor.node_id)
    ) {
      mejor = { node_id, nivel };
    }
  }

  // `mejor` nunca es null aquí (entidades.length > 0).
  return { ok: true, value: mejor!.node_id };
}

// ---------------------------------------------------------------------------
// Implementación de la interfaz Graph (variante demo, in-memory)
// ---------------------------------------------------------------------------

/**
 * Grafo_Entidades en memoria que implementa {@link Graph}. Se construye
 * desde una {@link ProyeccionGrafo} (típicamente la salida de
 * {@link buildGraphFromData}) y reacciona a eventos vía `upsertNode`/
 * `upsertEdge` (R11.3).
 */
export class GrafoEntidadesLocal implements Graph {
  private readonly nodos = new Map<Id, Nodo>();
  private readonly aristas: Arista[] = [];
  /** Clave única de arista para deduplicar upserts. */
  private readonly clavesArista = new Set<string>();
  private readonly config: GraphConfig;

  constructor(proyeccion?: ProyeccionGrafo, config: GraphConfig = GRAPH_CONFIG_DEFAULT) {
    this.config = config;
    if (proyeccion) {
      for (const n of proyeccion.nodos) this.nodos.set(n.node_id, n);
      for (const a of proyeccion.aristas) this.registrarArista(a);
    }
  }

  /** Construye el grafo demo directamente desde los datos crudos. */
  static fromData(data: GraphData, config: GraphConfig = GRAPH_CONFIG_DEFAULT): GrafoEntidadesLocal {
    return new GrafoEntidadesLocal(buildGraphFromData(data, config), config);
  }

  private claveArista(a: Arista): string {
    return `${a.origen_id}->${a.destino_id}:${a.tipo}`;
  }

  private registrarArista(a: Arista): void {
    const clave = this.claveArista(a);
    if (this.clavesArista.has(clave)) return;
    this.clavesArista.add(clave);
    this.aristas.push(a);
  }

  /** Proyección plana actual (nodos + aristas). */
  proyeccion(): ProyeccionGrafo {
    return { nodos: [...this.nodos.values()], aristas: [...this.aristas] };
  }

  async upsertNode(n: Nodo): Promise<Result<void>> {
    this.nodos.set(n.node_id, n);
    return { ok: true, value: undefined };
  }

  async upsertEdge(a: Arista): Promise<Result<void>> {
    this.registrarArista(a);
    return { ok: true, value: undefined };
  }

  /**
   * Reacciona a un Evento_Operacional del Event_Store proyectando (o
   * actualizando) el nodo de su entidad afectada en el grafo (R11.3).
   */
  async upsertFromEvent(e: EventoOperacional): Promise<Result<void>> {
    const ref = e.entidad_ref;
    const node_id = nodeIdFor(ref);
    const existente = this.nodos.get(node_id);
    const nodo: Nodo = existente
      ? { ...existente, attrs: { ...existente.attrs, ultimo_evento_ts: e.ts, ultimo_evento_tipo: e.tipo } }
      : { node_id, tipo: ref.tipo, entidad_ref: ref, attrs: { ultimo_evento_ts: e.ts, ultimo_evento_tipo: e.tipo } };
    return this.upsertNode(nodo);
  }

  async neighbors(node_id: Id, profundidad: number): Promise<Result<Subgrafo>> {
    return bfsNeighbors(this.proyeccion(), node_id, profundidad, this.config.maxProfundidad);
  }

  async rootOf(entidades: EntidadRef[]): Promise<Result<Id>> {
    return rootOf(entidades);
  }
}
